'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import KeyStats from '@/app/components/blog/KeyStats'
import CorrectionNote from '@/app/components/blog/CorrectionNote'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Clearly Filtered Remove Microplastics? Pitcher Verdict (2026)"
      author="MicroPlastics Research Desk"
      date="July 15, 2026"
      readTime="10 min read"
      heroImage="/blog/heroes/water.webp"
      heroImageAlt="Does Clearly Filtered remove microplastics — the Affinity pitcher audited against its independent lab testing"
    >
      <BlogLastUpdated iso="2026-09-21" formatted="September 21, 2026" />

      <CorrectionNote date="September 21, 2026">
        This page previously said no filter carries an NSF certification for microplastics and called Clearly Filtered the strongest microplastic pitcher. Three pitchers are certified for microplastics under NSF 401; Clearly Filtered&apos;s figure is lab-tested, not certified. It also said Clearly Filtered is tested to NSF 42, 53, 401 and 473 and that AquaTru holds &ldquo;P473&rdquo; with the highest certified removal. Clearly Filtered does not say which standards its lab tests followed; P473 was a PFOA/PFOS protocol; and AquaTru&apos;s certified microplastic figure is 96.48%, below PUR PLUS and Brita Elite.
      </CorrectionNote>


      <BlogTLDR>
        <strong>Yes, on lab tests, with one caveat about certification.</strong>{' '}
        Clearly Filtered&apos;s three-stage &ldquo;Affinity&rdquo; pitcher is
        independently lab-tested to remove <strong>99.9%+ of microplastics</strong>
        along with 365+ other contaminants (PFAS, lead, pharmaceuticals), and it is
        tested by an independent lab following NSF/ANSI protocols. Unlike reverse osmosis, it
        does this <strong>without stripping beneficial minerals</strong> like
        calcium and magnesium, so the water still tastes like water. The trade-offs
        are pitcher-shaped: slower gravity flow, limited capacity, and pricier
        replacement filters than a Brita. If you want serious microplastic removal
        with no counter space or plumbing, this is the pick.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Clearly Filtered&apos;s <strong>Affinity three-stage pitcher</strong> is independently tested to remove <strong>99.9%+ of microplastics</strong> plus PFAS, lead and pharmaceuticals.</>,
          <>It is <strong>certified</strong> only to NSF/ANSI 42 and 372, not to NSF/ANSI 401, the one standard with a microplastics claim.</>,
          <>Unlike reverse osmosis, it <strong>keeps beneficial minerals</strong> (calcium, magnesium), so the water isn&apos;t &ldquo;flat&rdquo; and needs no remineralizing.</>,
          <>It&apos;s a <strong>fridge pitcher</strong>: no plumbing, no power, no counter space, the easiest serious-filter format to live with.</>,
          <>Trade-offs: <strong>slower gravity flow, smaller capacity, and higher filter cost</strong> than a basic Brita, in exchange for far better removal.</>,
          <>Want faster throughput or fluoride-free water for the whole household? Compare with <Link href="/blog/does-aquatru-remove-microplastics" className="text-forest underline">AquaTru reverse osmosis</Link>.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_clearly_filtered" />

      <KeyStats
        heading="Clearly Filtered & microplastics — the facts"
        stats={[
          { value: '99.9%+', label: 'microplastic removal (independent lab)', context: 'third-party tested, not just manufacturer-claimed', source: 'Clearly Filtered performance data', sourceUrl: 'https://clearlyfiltered.com/pages/pitcher-performance-data' },
          { value: 'NSF 42, 372', label: 'certifications held', context: 'not certified to NSF/ANSI 401, the standard with a microplastics claim', source: 'NSF listing', sourceUrl: 'https://info.nsf.org/Certified/DWTU/Listings.asp?CompanyName=Clearly+Filtered&' },
          { value: 'Keeps minerals', label: 'unlike reverse osmosis', context: 'calcium and magnesium stay in — no flat taste, no remineralizing', source: 'Affinity filtration design', sourceUrl: 'https://www.clearlyfiltered.com/' },
          { value: 'Pitcher', label: 'no plumbing or power', context: 'lives in the fridge; the easiest serious filter to adopt', source: 'Clearly Filtered product design', sourceUrl: 'https://www.clearlyfiltered.com/' },
        ]}
      />

      <h2>Does Clearly Filtered remove microplastics?</h2>
      <p>
        Yes — and it is one of the few <em>pitchers</em> that can back the claim
        with independent testing. Clearly Filtered uses a proprietary three-stage
        &ldquo;Affinity&rdquo; filtration media rather than the single carbon block
        in a basic pitcher. The company publishes third-party laboratory results
        showing <strong>greater than 99.9% removal of microplastics</strong>, part
        of a tested list of 365+ contaminants that also includes PFAS, lead, and
        pharmaceutical residues.
      </p>
      <p>
        Clearly Filtered says the testing was done by an independent lab (QFT,
        now IAPMO New Jersey) that is capable of testing to <strong>NSF/ANSI
        protocols</strong>; it does not say which standard each test followed.
        NSF/ANSI 401 is the standard with a microplastics claim. The caveat:
        Clearly Filtered is not <em>certified</em> to it. Its NSF certifications cover chlorine
        taste and odour (NSF 42) and lead-free materials (NSF 372); the
        microplastic figure comes from a testing lab. Three pitchers do carry a
        certified NSF 401 microplastic claim: PUR PLUS, the Brita Elite
        cartridge and the LifeStraw Home glass pitcher. Clearly Filtered
        reports a higher figure than any of them, but as a lab result rather
        than a certified one. Against a standard Brita, which makes no
        microplastic claim at all, it is well ahead either way.
      </p>

      <h2>Why keeping minerals matters</h2>
      <p>
        The big functional difference between Clearly Filtered and a reverse-osmosis
        system like{' '}
        <Link href="/blog/does-aquatru-remove-microplastics" className="text-forest underline">AquaTru</Link>{' '}
        is what stays <em>in</em> the water. RO strips almost everything, including
        beneficial minerals, which is why RO water can taste flat and why many
        owners remineralize. Clearly Filtered&apos;s media is designed to pull out
        contaminants <strong>while leaving calcium and magnesium behind</strong>.
        For a lot of people that is the deciding factor: comparable microplastic
        removal, better-tasting water, and nothing to add back.
      </p>

      <h2>The trade-offs</h2>
      <p>
        Clearly Filtered is a pitcher, and pitchers have limits. The dense media
        that makes it effective also makes it <strong>slower to filter</strong> —
        gravity takes its time. Capacity is a pitcher&apos;s worth, so a large
        household refills often. And the replacement filters cost more than a
        Brita&apos;s, though they last longer and do far more. None of that changes
        the microplastic verdict — it is about whether a fridge pitcher fits your
        volume needs.
      </p>

      <h2>Clearly Filtered vs the alternatives</h2>
      <BlogComparisonTable
        caption="Clearly Filtered vs other microplastic filters"
        columns={[
          { key: 'filter', label: 'Filter' },
          { key: 'format', label: 'Format' },
          { key: 'proof', label: 'Evidence of removal' },
          { key: 'minerals', label: 'Keeps minerals?' },
          { key: 'best', label: 'Best for' },
        ]}
        rows={[
          { filter: 'Clearly Filtered', format: 'Fridge pitcher', proof: 'Brand-commissioned independent lab tests; certified to NSF 42, 372 only', minerals: 'Yes', best: 'No-install, keeps minerals' },
          { filter: 'AquaTru', format: 'Countertop RO', proof: 'Certified NSF/ANSI 401 for microplastics (96.48%), plus 42, 53, 58, 372 (IAPMO)', minerals: 'No (remineralize)', best: 'Certified microplastics plus lead, fluoride, arsenic' },
          { filter: 'Berkey', format: 'Gravity system', proof: 'Not tested for microplastics, per Berkey; uncertified', minerals: 'Yes', best: 'Off-grid / emergency' },
          { filter: 'Brita Standard', format: 'Pitcher', proof: 'NSF 42/53 (not 401)', minerals: 'Yes', best: 'Taste only, weak on microplastics' },
        ]}
      />
      <p>
        For the full breakdown see{' '}
        <Link href="/blog/microplastics-water-filters-compared" className="text-forest underline">Berkey vs Brita vs AquaTru vs Clearly Filtered</Link>{' '}
        and the{' '}
        <Link href="/blog/microplastics-water-filters-compared" className="text-forest underline">best water filters for microplastics</Link>{' '}
        guide. Curious how the gravity option holds up? Read the{' '}
        <Link href="/blog/does-berkey-filter-microplastics" className="text-forest underline">Berkey verdict</Link>.
      </p>

      <BlogAppCTA
        headline="Filtered at home — now check everywhere else"
        sub="Clearly Filtered covers your pitcher water. Scan bottled drinks, to-go cups, and food packaging with the MicroPlastics app to catch the exposure a pitcher can't reach."
      />

      <BlogFAQ
        items={[
          {
            question: 'Is Clearly Filtered NSF certified for microplastics?',
            answer: 'No. Clearly Filtered publishes independent lab results showing more than 99.99% microplastic reduction, but its NSF certifications cover only chlorine taste and odour (NSF 42) and lead-free materials (NSF 372). NSF/ANSI 401 does include a microplastics claim, and three pitchers are certified for it: PUR PLUS, the Brita Elite cartridge and the LifeStraw Home glass pitcher.',
          },
          {
            question: 'Does Clearly Filtered remove minerals from water?',
            answer: 'No. Unlike reverse osmosis, Clearly Filtered\'s Affinity media is designed to remove contaminants while leaving beneficial minerals like calcium and magnesium in the water, so it tastes normal and needs no remineralizing.',
          },
          {
            question: 'Is Clearly Filtered better than Brita for microplastics?',
            answer: 'Than a standard Brita, yes, by a wide margin: the white Brita cartridge makes no microplastic claim. Against the Brita Elite cartridge it is closer. The Elite is certified to NSF/ANSI 401 for 99.6% microplastic reduction; Clearly Filtered reports a higher figure and a broader contaminant list, but from lab testing rather than certification.',
          },
          {
            question: 'Clearly Filtered or AquaTru for microplastics?',
            answer: 'AquaTru (reverse osmosis) is certified to NSF/ANSI 401 for microplastics at 96.48% and also removes fluoride, but it strips minerals and needs counter space. Clearly Filtered reports a higher lab figure but holds no microplastics certification; it is a simpler fridge pitcher that keeps minerals. If you want a certified pitcher, PUR PLUS and Brita Elite are certified.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'NSF International', year: 2024, title: 'NSF/ANSI 401: Emerging contaminants / incidental compounds', publisher: 'NSF', url: 'https://www.nsf.org/consumer-resources/water-quality/water-filters-treatment' },
          { authors: 'Qian N, et al.', year: 2024, title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy', publisher: 'PNAS', url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121' },
          { authors: 'World Health Organization', year: 2022, title: 'Dietary and inhalation exposure to nano- and microplastic particles', publisher: 'WHO', url: 'https://www.who.int/publications/i/item/9789240054608' },
        ]}
      />
    </BlogPostLayout>
  )
}
